import React, { useContext, useState } from 'react';
import { UpdateWorkspace } from '../../../../shared/types/workspaceTypes';
import { FlexRow } from '../../../../shared/FlexRow';
import { FilledButton, TextButton } from '../../../../shared/styled-components/styled_buttons';
import styled from 'styled-components';
import { OnboardingStep1 } from './OnboardingStep1';
import { OnboardingStep2 } from './OnboardingStep2';
import { OnboardingStep1Preview } from './OnboardingStep1Preview';
import { OnboardingStep2Preview } from './OnboardingStep2Preview';
import { AppContext } from '../../../../App';
import { postCreateCompany } from '../../../../common/api/apiClient';
import { useTrack } from '@hackler/react-sdk';

interface Props {
  data: UpdateWorkspace;
  step: number;
  setStep: (step: number) => void;
  onChange: (data: UpdateWorkspace) => void;
}

export const OnboardingMobile: React.FC<Props> = ({ data, step, setStep, onChange }) => {
  const { redirect } = useContext(AppContext);
  const track = useTrack();
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const disabled = step === 1 && (!data.name || !data.contact || !data.location || !data.location.address || !data.location.address_detail);

  const handleClick = () => {
    track({ key: 'click_step2_start' });

    postCreateCompany(
      {
        name: data.name,
        officer_contact: data.contact.officer_contact,
        address: data.location.address,
        address_detail: data.location.address_detail,
        x: String(data.location.x),
        y: String(data.location.y),
        zoom_lv: data.location.zoom_lv,
        parking: data.location.parking,
        signature: data.tree_signature,
      },
      redirect,
    );
  };

  return (
    <Container>
      <Wrapper>
        <ProgressBarWrapper>
          <CharacterWrapper>
            <FlexRow style={{ transform: 'translateY(-0.8rem)' }}>
              <TooltipBubble>{step === 1 ? '다음 1단계만 남았어요!' : '마지막 단계예요!'}</TooltipBubble>
              <TooltipBubbleArrow />
            </FlexRow>
            <Character src={'/images/workspace-wizard/create_progress_character.png'} alt={'character'} />
          </CharacterWrapper>
          <ProgressBar>
            <ProgressFill $step={step} />
            {step === 2 && <ProgressFillRight />}
          </ProgressBar>
        </ProgressBarWrapper>
        {step === 1 && <OnboardingStep1 data={data} onChange={onChange} previewClick={() => setShowPreview(true)} />}
        {step === 2 && <OnboardingStep2 data={data} onChange={onChange} previewClick={() => setShowPreview(true)} />}
      </Wrapper>
      {!showPreview && (
        <Footer>
          {step === 1 ? (
            <FilledButton
              size={'l'}
              color={'primary'}
              icon={{ slot: 'end', src: '/images/common/chevron.svg', rotate: '180deg' }}
              style={{ width: '100%' }}
              disabled={disabled}
              onClick={() => {
                if (!data.name || !data.contact || !data.location || !data.location.address || !data.location.address_detail) return;
                track({ key: 'click_step1_next' });
                setStep(2);
              }}
            >
              다음 단계
            </FilledButton>
          ) : (
            <FlexRow width={'100%'} justify={'space-between'} align={'center'} gap={'1.2rem'}>
              <TextButton
                size={'l'}
                color={'mono'}
                icon={{ slot: 'start', src: '/images/common/chevron.svg' }}
                onClick={() => {
                  track({ key: 'click_step2_back' });
                  setStep(1);
                }}
              >
                이전
              </TextButton>
              <FilledButton size={'l'} color={'primary'} style={{ flex: 1 }} onClick={handleClick}>
                시작하기
              </FilledButton>
            </FlexRow>
          )}
        </Footer>
      )}
      {step === 1 && showPreview && <OnboardingStep1Preview data={data} handleClose={() => setShowPreview(false)} />}
      {step === 2 && showPreview && <OnboardingStep2Preview data={data} handleClose={() => setShowPreview(false)} />}
    </Container>
  );
};

const Container = styled.div`
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background-color: var(--background-2);
`;

const Wrapper = styled.div`
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  padding: 0 1.6rem 8rem;
  overflow-y: auto;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  gap: 2.4rem;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 6.8rem;
  min-height: 6.8rem;
  margin-top: 1.6rem;
  position: relative;

  display: flex;
  align-items: flex-end;
`;

const CharacterWrapper = styled.div`
  position: absolute;
  top: 0.81rem;
  right: 0;
  z-index: 2;

  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 0.6rem;
`;

const Character = styled.img`
  width: 6rem;
  height: 5.5636rem;
`;

const TooltipBubble = styled.div`
  width: fit-content;
  padding: 0.6rem 0.8rem;
  border-radius: 4px;
  background-color: #1b1d1fe5;

  color: white;
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 160%;
`;

const TooltipBubbleArrow = styled.div`
  width: 0;
  height: 0;
  border-top: 0.9rem solid transparent;
  border-bottom: 0.9rem solid transparent;
  border-left: 0.9rem solid #1b1d1fe5;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 1.2rem;
  background-color: var(--background-3);
  border-radius: 100rem;
  position: relative;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $step: number }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 50%;
  background-color: ${props => (props.$step === 1 ? '#AFCAFF' : '#3a6cff')};
  border-radius: 100rem;
  z-index: 3;
`;

const ProgressFillRight = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 55%;
  background-color: #afcaff;
  border-radius: 0 100rem 100rem 0;
`;

const Footer = styled.div`
  width: 100%;
  min-height: 6.8rem;
  padding: 1.2rem 1.6rem;
  background-color: var(--background-1);
  border-top: 1px solid var(--border-color);
  position: sticky;
  bottom: 0;
  z-index: 10;
  box-sizing: border-box;
`;
